'use client';

import { useState, useEffect } from 'react';
import { cryptoPricesSubscription } from './market-data-subscriptions';

// TypeScript interfaces based on the Firestore data structure
export interface CryptoPriceData {
  currentPrice: number;
  priceChange24h: number;
  priceChangePercentage24h?: number;
  volume24h: number;
  marketCap: number;
  lastUpdated: string;
}

export interface AbstractToken {
  symbol: string;
  name: string;
  address: string;
  currentPrice: number;
  priceChange24h: number;
  priceChangePercentage24h?: number;
  priceChange1h?: number;
  priceChange5m?: number;
  priceChange6h?: number;
  volume24h: number;
  volume6h?: number;
  volume1h?: number;
  lastUpdated: string;
  // Image and metadata fields
  imageUrl?: string;
  ogImage?: string;
  chart24hUrl?: string;
  tradingUrl?: string;
  ogUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  // Market data
  marketCap?: number;
  fdv?: number;
  liquidity?: number;
  pairAddress?: string;
  // Transaction counts - buy/sell pressure
  txns24h?: {
    buys: number;
    sells: number;
    total: number;
  };
  // Pair creation timestamp - token age
  pairCreatedAt?: number;
  // Social links from DexScreener
  websiteUrl?: string;
  twitterUrl?: string;
  telegramUrl?: string;
  discordUrl?: string;
  // Admin management fields
  featured?: boolean;
  priority?: number;
  description?: string;
  customImage?: string;
  // Whether token is native to Abstract chain
  isAbstractNative?: boolean;
  // Associated tag slug for linking to entity pages
  tagSlug?: string;
  // Multi-pool aggregation data
  poolCount?: number;
  pools?: Array<{
    dex: string;
    pairAddress: string;
    quoteToken: string;
    liquidity: number;
    volume24h: number;
    price: number;
  }>;
}

export interface FirestoreCryptoData {
  bitcoin?: CryptoPriceData;
  ethereum?: CryptoPriceData;
  abstract_tokens: AbstractToken[];
  /**
   * Per-chain token arrays, keyed by chain id — the multichain price schema written by
   * CryptoStreamService alongside the legacy flat `abstract_tokens`. It was already in the
   * `crypto_prices` doc and already reached the browser (the /api/market/snapshot route
   * passes the whole document through), but was missing from this type, so the client had
   * no way to see any chain's tokens but Abstract's.
   *
   * Read it with the same precedence the server uses (`resolveChainTokenArray` in
   * src/lib/hub/chain-hub-data.ts): `tokensByChain[chainId]` first, flat `<chainId>_tokens`
   * as the fallback. A chain absent from this map genuinely has NO priced tokens — which is
   * what lets the market lens derive "does this chain have a token feed" from DATA rather
   * than from a hardcoded chain id.
   */
  tokensByChain?: Record<string, AbstractToken[]>;
  timestamp: string;
}

/**
 * Resolve the token array for a chain LENS, client-side.
 *
 * The browser mirror of `resolveChainTokenArray` (src/lib/hub/chain-hub-data.ts): prefer the
 * per-chain `tokensByChain[chainId]` schema, fall back to the legacy flat `abstract_tokens`.
 * Lives here, next to the type, because two components (MarketTerminal and its child
 * UnifiedAssetDirectory) each resolve tokens independently — they previously carried
 * SEPARATE copies of a hardcoded `chainId !== 'abstract' → no tokens` rule, which is exactly
 * how the two halves of the market lens drift apart.
 *
 * @param chainId - the validated lens chain id, or undefined for the cross-chain view
 * @returns the lens's tokens; [] when the chain has none (render as ABSENT, never as
 *          another chain's numbers wearing this lens's label)
 */
export function resolveLensTokens(
  data: FirestoreCryptoData | null | undefined,
  chainId?: string,
): AbstractToken[] {
  if (!data) return [];
  const key = chainId ?? 'abstract';
  const byChain = data.tokensByChain;
  if (byChain && Array.isArray(byChain[key])) return byChain[key];
  // Cross-chain ("All chains") and Abstract both fall back to the legacy flat array; any
  // other chain with no `tokensByChain` entry genuinely has no priced tokens.
  return !chainId || key === 'abstract' ? (data.abstract_tokens ?? []) : [];
}

interface UseFirestoreCryptoOptions {
  enableRealTimeUpdates?: boolean;
}

interface UseFirestoreCryptoReturn {
  data: FirestoreCryptoData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  isRealTime: boolean;
}

export function useFirestoreCrypto(options: UseFirestoreCryptoOptions = {}): UseFirestoreCryptoReturn {
  const { enableRealTimeUpdates = true } = options;

  const [data, setData] = useState<FirestoreCryptoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    // Skip during build/SSR — the shared poller fetches a browser-only route.
    if (!enableRealTimeUpdates || typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    setError(null);

    // Attach to the ONE shared market-snapshot poller (see market-data-subscriptions).
    // Every mount of this hook used to open its own Firestore onSnapshot; they now share a
    // single reference-counted 60s poll of the cached /api/market/snapshot route, so N
    // components on a page = 0 per-browser Firestore reads.
    const unsubscribe = cryptoPricesSubscription.subscribe(
      (cryptoData) => {
        setData(cryptoData);
        setLastUpdated(new Date());
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(`Failed to fetch crypto prices: ${err.message}`);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [enableRealTimeUpdates]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    isRealTime: enableRealTimeUpdates,
  };
}

// Helper function to format crypto data for components (maintains compatibility)
export function formatCryptoDataForDisplay(firestoreData: FirestoreCryptoData | null) {
  if (!firestoreData) return null;

  return {
    data: {
      bitcoin: firestoreData.bitcoin,
      ethereum: firestoreData.ethereum,
      abstract_tokens: firestoreData.abstract_tokens || [],
      last_updated: firestoreData.timestamp,
    },
    meta: {
      total_tokens: (firestoreData.bitcoin ? 1 : 0) + (firestoreData.ethereum ? 1 : 0) + (firestoreData.abstract_tokens?.length || 0),
      abstract_tokens_count: firestoreData.abstract_tokens?.length || 0,
      api_version: 'firestore-1.0',
    },
  };
}
